import {
  sectorContextQuestions,
  dependsOnSatisfied,
} from "../dataHelpers/sectorContextQuestions";

// AI-CONTEXT: See docs/features/sector-context-questions.md
// AI-CONTEXT: See docs/pitfalls.md#state-handler-overwrites-sibling-field

/**
 * The Firestore update for one answer change, including any answers that the
 * change orphans.
 *
 * Two rules are baked in, and both are the reason this is a helper rather than
 * inline code:
 *
 * 1. **Dot-paths, never a nested object.** Writing
 *    `{ SectorContext: { energiearmoede: {...} } }` replaces the whole map and
 *    wipes the other twelve answers.
 * 2. **One update, not two.** When a parent stops matching, its dependent
 *    question disappears from the UI — but its stored answer would live on in
 *    the benchmark where the user can no longer see or correct it. It has to be
 *    cleared, and clearing it in a second write is the sibling-clobber pitfall.
 *
 * Orphans are found by recomputing visibility rather than by looking up direct
 * children, so a chain (a child of a child) also unwinds. Questions are
 * evaluated in definition order, which the taxonomy's test guarantees is
 * parents-first.
 *
 * The sweep runs over the whole sector on every write, not just over this
 * question's children — so an unrelated answer can also clear an orphan left
 * behind by an earlier change. That is deliberate: it makes the state
 * self-healing rather than depending on the write that created the orphan.
 *
 * @param {object} params
 * @param {string} params.sector - Sector slug; the answers are nested under it.
 * @param {string} params.questionId - The question the user just answered.
 * @param {string|null} params.value - The new answer; null/undefined clears it.
 * @param {Record<string, string>} [params.answers] - This sector's answers BEFORE the change.
 * @param {*} params.deleteSentinel - What to write to remove a key, normally
 *   `firebase.firestore.FieldValue.delete()`. Injected so this stays pure and
 *   testable — a stored `null` would read back as an answer.
 * @returns {Record<string, *>} Dot-path → value, ready for `.update()`.
 */
const buildSectorContextUpdate = ({ sector, questionId, value, answers, deleteSentinel }) => {
  const path = (id) => `SectorContext.${sector}.${id}`;
  const clearing = value === null || value === undefined;

  const update = { [path(questionId)]: clearing ? deleteSentinel : value };

  // The answer set as it will be once this change lands — what visibility is
  // judged against.
  const next = { ...(answers || {}) };
  if (clearing) delete next[questionId];
  else next[questionId] = value;

  const config = sectorContextQuestions[sector];
  if (!config) return update;

  config.groups
    .flatMap((group) => group.questions)
    .forEach((question) => {
      if (question.id === questionId) return;
      // Nothing stored → nothing to orphan.
      if (next[question.id] === undefined) return;
      // Still visible → the answer is still the user's to see and change.
      if (dependsOnSatisfied(question, next)) return;

      update[path(question.id)] = deleteSentinel;
      // Drop it from `next` too, so anything depending on THIS question is
      // judged against the cleared state and unwinds in the same pass.
      delete next[question.id];
    });

  return update;
};

export default buildSectorContextUpdate;
